import {Button, Col, DatePicker, Form, Alert, Modal, notification, Row, Select} from "antd";
import React, {useState, useEffect} from "react";
import { useTranslation } from "react-i18next";
import { coreApi } from "../../api/calls";
import TextArea from "antd/lib/input/TextArea";
import EmployeeSelect from "../Employees/EmployeeSelect";
import ContactSelect from "../Contacts/ContactSelect";
import moment from "moment/moment";
import PartnerSelect from "../Partners/PartnerSelect";
import { EditOutlined } from "@ant-design/icons";


const gutter = [8, 0];
const MAX_CHARACTERS = 15000;
const MIN_CHARACTERS = 10;
const communication_cases = ['phone', 'email'];
const communication_types = ['review', 'verification', 'finance', 'operational', 'contract', 'other'];

/**
 * @component
 * @alias BuildingEditCommunication
 * @prop {object} record - communication record to edit
 * @prop {number} buildingId - building ID for ContactSelect
 * @prop {number} defaultPartnerId - default partner ID
 * @prop {function} onUpdate - callback after successful update
 */

const BuildingEditCommunication = ({ record, buildingId, defaultPartnerId = null, onUpdate }) => {
  const { t } = useTranslation();

  const [isModalOpen, setModalOpen] = useState(false);
  const [text, setText] = useState('');
  const [error, setError] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    let isMounted = true;
    
    if (isModalOpen && record) {
      const caseTags = [];
      const typeTags = [];

      if (record.case && record.case !== 'null') {
        caseTags.push(record.case);
      }
      if (record.tags && record.tags.length > 0) {
        record.tags
          .filter(tag => tag.type === 'communication_case' && tag.key && tag.key !== 'null')
          .forEach(tag => {
            if (!caseTags.includes(tag.key)) {
              caseTags.push(tag.key);
            }
          });
      }

      if (record.type && record.type !== 'null') {
        typeTags.push(record.type);
      }
      if (record.tags && record.tags.length > 0) {
        record.tags
          .filter(tag => tag.type === 'communication_type' && tag.key && tag.key !== 'null')
          .forEach(tag => {
            if (!typeTags.includes(tag.key)) {
              typeTags.push(tag.key);
            }
          });
      }

      if (isMounted) {
        const descriptionValue = record.description || record.body_html || record.body_plain || record.subject || '';
        
        form.setFieldsValue({
          created_at: moment(record.created_at),
          case_tag_ids: caseTags,
          type_tag_ids: typeTags,
          to: record.contact_id || record.to || "other",
          body_html: descriptionValue,
          from: record.from || JSON.parse(localStorage.getItem("user")).id,
          partner_id: record.partner_id || defaultPartnerId
        });
        
        setText(descriptionValue);
      }
    }
    
    return () => {
      isMounted = false;
    };
  }, [isModalOpen, record, form]);

  const updateCommunication = (values) => {
    if (!record.id) {
      notification.error({ message: t("common.error") + ": Missing record ID" });
      return;
    }

    const payload = {
      from: values.from,
      to: values.to === "other" ? null : values.to,
      body_html: values.body_html,
      created_at: values.created_at,
      case_tag_ids: values.case_tag_ids || [],
      type_tag_ids: values.type_tag_ids || [],
      partner_id: values.partner_id,
      building_id: record.building_id || buildingId,
    };

    coreApi
      .put("/emails/buildings/" + record.id, payload)
      .then((response) => {
        notification.success({ message: t("emails.communication_updated") });
        onUpdate();
        setModalOpen(false);
      })
      .catch((error) => {
        notification.error({ message: error?.response?.data?.message || t("common.error") });
      });
  };

  const handleTextChange = (e) => {
    const newText = e.target.value;
    setText(newText);
    setError(newText.length > MAX_CHARACTERS);
    form.setFieldsValue({ body_html: newText });
  };

  return (
    <React.Fragment>
      <Button 
        icon={<EditOutlined />} 
        size="small"
        onClick={() => { setModalOpen(true);}}
      />
      <Modal
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
          setText('');
        }}
        onOk={() => {
          form.submit();
        }}
        okText={t("common.save")}
        open={isModalOpen}
        title={t("emails.communication_edit_title")}
      >
        <Form
          form={form}
          onFinish={async (values) => {
            await updateCommunication(values);
            form.resetFields();
            setText('');
          }}
          layout={"horizontal"}
        >
          <Row gutter={gutter}>
            <Col span={24}>
              <Form.Item
                name={"created_at"}
                label={t("emails.date")}
                rules={[{required: true}]}
              >
                <DatePicker />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item 
                name="case_tag_ids"
                label={t("emails.communication")}
                rules={[{ required: true, message: t("emails.communication_required") }]}
              >
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder={t("emails.communication_placeholder")}
                >
                  {communication_cases.map((type) => (
                    <Select.Option value={type} key={type}>
                      {t("emails.communication_cases." + type)} {type}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item 
                name="type_tag_ids"
                label={t("emails.communication_type")}
                rules={[{ required: true, message: t("emails.communication_type_required") }]}
              >
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder={t("emails.communication_type_placeholder")}
                >
                  {communication_types.map((type) => (
                    <Select.Option key={type} value={type}>
                      {t("emails.types." + type)}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <ContactSelect
                id={record.building_id || buildingId}
                label={t("emails.with")}
                required={true}
                showOtherContact={true}
              />
            </Col>
          </Row>
          <Row gutter={gutter}>
            <Col span={24}>
              <Form.Item
                name="body_html"
                label={t("emails.description")}
                rules={[
                  { required: true, message: t("emails.description_required") },
                  { min: MIN_CHARACTERS, message: t("emails.description_min_length") }
                ]}
                validateStatus={error ? 'error' : ''}
                help={
                  error && (
                    <Alert
                      message={t("emails.communication_text_error")}
                      type="error"
                      showIcon
                      style={{ marginBottom: 8 }}
                    />
                  )
                }
              >
                <TextArea
                  rows={4}
                  value={text}
                  onChange={handleTextChange}
                  showCount={{
                    formatter: ({ count }) => {
                      if (count > 0 && count < MIN_CHARACTERS) {
                        return `${count}/${MAX_CHARACTERS} ⚠️ ${t('emails.description_remaining')}`;
                      }
                      return `${count}/${MAX_CHARACTERS}`;
                    },
                  }}
                  style={{
                    borderColor: text.length > 0 && text.length < MIN_CHARACTERS ? '#ff4d4f' : undefined
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={gutter}>
            <Col span={24}>
              <EmployeeSelect
                required={true}
                name={"from"}
                label={t("common.user")}
                activeUsers={true}
                placeholder={''}
                initialValue={record.from || JSON.parse(localStorage.getItem("user")).id}
              />
            </Col>
          </Row>
          <Row gutter={gutter}>
            <Col span={24}>
              <PartnerSelect
                label={t("partners.partner")}
                name={"partner_id"}
                required={true}
                initialValue={record.partner_id || defaultPartnerId}
              />
            </Col>
          </Row>
        </Form>
      </Modal>
    </React.Fragment>
  );
};

export default BuildingEditCommunication;
